/**
 * ChildFormRegistry Service - Zustand Store
 * Single Responsibility: Track registered child forms
 */

import { create } from 'zustand';
import type { FormEngine } from '../types/index';

export interface ChildFormRegistryState {
  forms: Map<string, FormEngine>;
}

export interface ChildFormRegistryActions {
  /**
   * Register a child form
   */
  register: (formID: string, engine: FormEngine) => void;

  /**
   * Unregister a child form
   */
  unregister: (formID: string) => void;

  /**
   * Get a registered form by ID
   */
  get: (formID: string) => FormEngine | undefined;

  /**
   * Get all registered forms
   */
  getAll: () => FormEngine[];

  /**
   * Unregister all forms
   */
  unregisterAll: () => void;

  /**
   * Check if a form is registered
   */
  has: (formID: string) => boolean;
}

export type ChildFormRegistryStore = ChildFormRegistryState &
  ChildFormRegistryActions;

export const useChildFormRegistryStore = create<ChildFormRegistryStore>(
  (set, get) => ({
    forms: new Map<string, FormEngine>(),

    register: (formID: string, engine: FormEngine): void => {
      set((state) => {
        const newForms = new Map(state.forms);
        newForms.set(formID, engine);
        return { forms: newForms };
      });
    },

    unregister: (formID: string): void => {
      set((state) => {
        const newForms = new Map(state.forms);
        newForms.delete(formID);
        return { forms: newForms };
      });
    },

    get: (formID: string): FormEngine | undefined => {
      return get().forms.get(formID);
    },

    getAll: (): FormEngine[] => {
      return Array.from(get().forms.values());
    },

    unregisterAll: (): void => {
      set({ forms: new Map() });
    },

    has: (formID: string): boolean => {
      return get().forms.has(formID);
    },
  })
);
